import {
  IonButton,
  IonContent,
  IonIcon,
  IonImg,
  IonInput,
  IonSpinner,
  IonToolbar
} from '@ionic/react';
import { HKPlatform } from 'core/constants';
import { RootState } from 'core/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cameraOutline, alertOutline } from 'ionicons/icons';
import { usePhotoGallery, UserPhoto } from 'core/hooks/usePhotoGallery';
import { useForm } from 'react-hook-form';
import {
  getFirebaseDownloadUrl,
  useFirebaseUpload
} from 'core/hooks/useFirebase';
import { displayError, displaySuccess } from 'core/services/AlertActions';
import {
  hideAddItemModal,
  resetProductTemplate,
  resetManufacturers,
  setAddItemShowLanding,
  setInventoryLoading
} from 'features/shared/inventory/store/InventorySlice';
import './InventoryAddProduct.scss';
import {
  createInventoryProduct,
  fetchProductTemplate,
  fetchProductTypeGroups,
  searchManufacturers
} from 'features/shared/inventory/store/InventoryActions';
import {
  AttributeSchema,
  ProductBodyDetails,
  ProductModelSearchResult
} from 'core/types';
import { gaBtnClickEvent, gaModalView } from 'core/util';
import InventoryAddItemLanding from 'features/shared/inventory/components/InventoryAddItemLanding';
import HkModalHeader from 'components/modal/HkModalHeader';
import ProductModelSerialStepFields from 'features/shared/inventory/components/ProductModelSerialStepFields';
import ProductBrandStepField from 'features/shared/inventory/components/ProductBrandStepField';
import ProductGroupTypeSelects from 'features/shared/inventory/components/ProductGroupTypeSelects';

export enum ProductAttributeType {
  STRING = 'string',
  TAGGED = 'tagged'
}

export enum PhotoType {
  MODEL = 'model',
  BRAND = 'brand'
}

const InventoryAddProduct: React.FC = () => {
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const {
    loading,
    productTypeGroupsList,
    productTemplate,
    addItemShowLanding,
    manufacturers
  } = useSelector((state: RootState) => state.inventory);
  const { isDigital } = useSelector((state: RootState) => state.user);
  const { takePhoto } = usePhotoGallery();
  const { uploadFile } = useFirebaseUpload();
  const [modelPhoto, setModelPhoto] = useState<UserPhoto | null>();
  const [brandPhoto, setBrandPhoto] = useState<UserPhoto | null>();
  const [selectedProductTags, setSelectedProductTags] = useState<any[]>([]);
  const [selectedProductGroup, setSelectedProductGroup] = useState<
    number | null
  >();
  const [selectedProductType, setSelectedProductType] = useState<
    number | null
  >();
  // A specific catalog Model # picked via ProductModelSearchSelect (Step 1) —
  // wins over selectedManufacturerId if both are set (more specific). Cleared
  // whenever Group/Type changes, since a different Type invalidates it.
  const [selectedProductTemplateId, setSelectedProductTemplateId] = useState<
    number | null
  >(null);
  const [selectedProductTemplateLabel, setSelectedProductTemplateLabel] =
    useState<string | null>(null);
  // A Brand/Manufacturer picked via HKSelect (Step 2) with no
  // specific Model # — the API resolves this to a found-or-created
  // "Default/Generic" template for that brand+type.
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<
    number | null
  >(null);
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);

  const dispatch = useDispatch();

  // Add Item landing (Figma "Add Item Landing"): model-search is the primary
  // path, with "Manual Add Item" falling through to the existing Group/Type
  // driven flow below. See InventoryAddItemLanding for the model→type match.
  // Lives in Redux (not local state) because InventoryGroups.tsx/
  // InventoryProducts.tsx — which own the outer IonModal this component is
  // rendered inside of — need to read it too, to toggle the modal's
  // 496px/740px --height (theme/variables.scss).
  const showLanding = addItemShowLanding;
  const setShowLanding = (value: boolean) =>
    dispatch(setAddItemShowLanding(value));

  const { register, handleSubmit, reset } = useForm();

  /**
   * A model picked on the landing's "Enter Item Model" search prefills the
   * manual form's Group, Type, Model #, and Brand/Manufacturer (D20) — not
   * just Group/Type. Every one of those comes off the search result as an id:
   * `result.id` is a real `product_template_id` and `result.manufacturer_id` a
   * real `manufacturers.id`, so nothing here has to be resolved from a name.
   * The Brand select's own option list is populated separately by the
   * `selectedProductType` effect's `searchManufacturers(typeId)` fetch — this
   * only needs to say *which* id is selected, not wait for that list.
   */
  const onModelMatched = (
    groupId: number,
    typeId: number,
    result: ProductModelSearchResult
  ) => {
    setSelectedProductGroup(groupId);
    setSelectedProductType(typeId);
    setSelectedProductTemplateId(result.id);
    setSelectedProductTemplateLabel(result.model);
    setSelectedManufacturerId(result.manufacturer_id);
    setShowLanding(false);
  };

  const selectProductTag = (field: string, tag: any) => {
    const keyExists = selectedProductTags.some((item: any) => {
      return field in item;
    });
    if (keyExists) {
      if (selectedProductTags.every((item: any) => item[field] === tag)) {
        setSelectedProductTags([]);
      } else {
        const existingTagIndex = selectedProductTags.findIndex((item: any) => {
          return field in item;
        });
        setSelectedProductTags(selectedProductTags.splice(existingTagIndex, 1));
        setSelectedProductTags([...selectedProductTags, { [field]: tag }]);
      }
    } else {
      setSelectedProductTags([...selectedProductTags, { [field]: tag }]);
    }
  };

  const checkIfProductTagSelected = (field: string, tag: any) => {
    return selectedProductTags.some((item: any) => {
      return item[field] === tag;
    });
  };

  const addPhoto = (type: string) => {
    takePhoto()
      .then((photo) => {
        if (!!homeId && !!photo.filePath && !!photo.webviewPath) {
          photo.filePath = `product_${new Date().getTime()}_catalog_${
            type === PhotoType.MODEL ? '1' : '2'
          }.jpg`;
          if (type === PhotoType.MODEL) {
            setModelPhoto(photo);
          } else if (type === PhotoType.BRAND) {
            setBrandPhoto(photo);
          }
        }
      })
      .catch((error) => {
        console.log(`Upload Photo Error: ${error}`);
      });
  };

  const uploadPhotos = async () => {
    dispatch(setInventoryLoading(true));
    const uploads: any[] = [];
    if (!!homeId && !!modelPhoto) {
      uploads.push(
        uploadFile(homeId, modelPhoto.filePath, modelPhoto.imageData).catch(
          (e) => {
            console.log(e);
            displayError(e);
            throw e;
          }
        )
      );
    }
    if (!!homeId && !!brandPhoto) {
      uploads.push(
        uploadFile(homeId, brandPhoto.filePath, brandPhoto.imageData).catch(
          (e) => {
            console.log(e);
            displayError(e);
            throw e;
          }
        )
      );
    }
    return await Promise.all(uploads);
  };

  const addProduct = (formData: any) => {
    gaBtnClickEvent('add_inventory_product');
    let promises: any[] = [];
    const productBody: ProductBodyDetails = {
      home_id: homeId,
      type: productTemplate?.field,
      attributes: {}
    };

    // Create PHOTO for POST body (Upload to firebase storage)
    uploadPhotos().then(
      (uploadedImages) => {
        for (const image of uploadedImages) {
          promises.push(
            getFirebaseDownloadUrl(image.metadata.fullPath).then((url) => {
              if (image.metadata.name.toLowerCase().includes('catalog_1')) {
                productBody['model_photo'] = url;
              }
              if (image.metadata.name.toLowerCase().includes('catalog_2')) {
                productBody['brand_photo'] = url;
              }
            })
          );
        }

        // Create TAGGED attributes from availableProductTags for POST body
        if (selectedProductTags.length > 0) {
          selectedProductTags.forEach((tag) => {
            for (const [key, value] of Object.entries(tag)) {
              productBody.attributes[key] = [value];
            }
          });
        }

        // Create STRING attributes from formData for POST body — "serial" is
        // excluded here since it's the dedicated Step 1 field below, mapped
        // directly to Products.serial rather than a dynamic attribute.
        if (!!productTemplate?.product_attribute_schema.length) {
          productTemplate.product_attribute_schema
            .concat(productTemplate.attribute_schema)
            .filter(
              (question: AttributeSchema) =>
                question.field.toLowerCase() !== 'serial'
            )
            .forEach((question: AttributeSchema) => {
              Object.entries(formData).forEach((datum) => {
                const [key, value] = datum;
                if (question.field === key) {
                  productBody.attributes[question.field] = value;
                }
              });
            });
        }

        // Serial Number (Step 1) maps directly to Products.serial, not a
        // dynamic attribute — see ProductBodyDetails.serial.
        if (formData.serial) {
          productBody.serial = formData.serial;
        }

        // Model Number (Step 1) wins over Brand (Step 2) if both are picked —
        // it's the more specific catalog selection. Neither picked keeps the
        // existing Group/Type-driven generic-template fallback unchanged.
        if (selectedProductTemplateId) {
          productBody.product_template_id = selectedProductTemplateId;
        } else if (selectedManufacturerId) {
          productBody.manufacturer_id = selectedManufacturerId;
        }

        // Create Product and Reset Form here
        if (!!homeId && !!productBody) {
          Promise.all(promises)
            .then(async () => {
              // Awaited — the modal's onDidDismiss (fired once hideAddItemModal
              // below finishes the close animation) refetches the group counts.
              // Not awaiting this left that refetch racing the CREATE request:
              // the count could be requested before the new product was
              // actually committed, silently returning a stale (pre-add) count.
              await dispatch(createInventoryProduct(homeId, productBody));
              resetProductForm();
              dispatch(displaySuccess('Product Added', 'Success'));
              dispatch(hideAddItemModal());
            })
            .catch((error: any) => {
              console.log(error);
              // uploadPhotos() set the slice's loading flag and nothing else
              // clears it on this path — createProductSuccess only fires on
              // success. Leaving it set locks the whole modal body and the
              // submit button behind a spinner, and this IonModal is
              // backdropDismiss={false}, so the user can't retry or back out.
              dispatch(setInventoryLoading(false));
              dispatch(
                displayError(
                  error?.message || 'Unable to add this item. Please try again.'
                )
              );
            });
        }
      },
      (error) => {
        // Same stuck-loading problem as the .catch above — uploadPhotos()
        // rejected, so its own loading flag is still set.
        dispatch(setInventoryLoading(false));
        dispatch(displayError(error));
      }
    );
  };

  const cancelAddProduct = () => {
    gaBtnClickEvent('cancel_add_inventory_product');
    dispatch(hideAddItemModal());
    resetProductForm();
  };

  // "Back" (Figma node 8687:5940) — clears the manual form and returns to
  // the landing's "Enter a model number to get started..." search, without
  // closing the modal (unlike Cancel, which also dispatches
  // hideAddItemModal()).
  const goBackToModelSearch = () => {
    gaBtnClickEvent('back_to_model_search_add_inventory_product');
    resetProductForm();
  };

  const resetProductForm = () => {
    setModelPhoto(null);
    setBrandPhoto(null);
    setSelectedProductType(null);
    setSelectedProductGroup(null);
    setSelectedProductTemplateId(null);
    setSelectedProductTemplateLabel(null);
    setSelectedManufacturerId(null);
    // Clears the Serial Number field (react-hook-form state — not covered by
    // the setState calls above).
    reset();
    setShowLanding(true);
  };

  // Does NOT reset selectedProductTemplateId/Label/ManufacturerId — those are
  // reset explicitly by the Group/Type selects' own onIonChange (a *manual*
  // Group/Type change should clear a prior catalog pick), not here, since
  // this effect also fires when onModelMatched sets selectedProductType
  // programmatically (from the landing's search, D20) — which sets those
  // same 3 fields in that same call and must not have them immediately
  // clobbered back to null on the next render.
  useEffect(() => {
    dispatch(resetProductTemplate());
    dispatch(resetManufacturers());
    if (!!selectedProductType) {
      dispatch(fetchProductTemplate(selectedProductType));
      dispatch(searchManufacturers(selectedProductType));
    }
  }, [selectedProductType]);

  useEffect(() => {
    if (!!productTypeGroupsList && productTypeGroupsList.length === 0) {
      dispatch(fetchProductTypeGroups());
    }
  }, [productTypeGroupsList]);

  useEffect(() => {
    gaModalView(isDigital, '/inventory/group/product/add');
  }, []);

  function AddProductQuestions() {
    return (
      <>
        {!!productTemplate && (
          // Photo Questions
          <div className="hk-digital-add-product-body-steps">
            <div className="hk-digital-add-product-body-steps-card">
              <div className="hk-digital-add-product-body-steps-card-title">
                1: Take a picture of Model Number and Serial Number Details
              </div>
              <div className="hk-digital-add-product-body-steps-card-subtitle">
                <IonIcon icon={alertOutline} />
                <span>required task</span>
              </div>
              <div className="hk-digital-add-product-body-steps-card-description">
                <span>
                  This can be found on the inside or outside of the{' '}
                  {productTemplate.label}.
                </span>
                <div className="hk-digital-add-product-body-steps-card-description-image">
                  {!!modelPhoto && (
                    <>
                      <IonImg src={modelPhoto.webviewPath} />
                      <input
                        name="model_photo"
                        ref={register}
                        type="hidden"
                        value={modelPhoto.webviewPath}
                      />
                    </>
                  )}
                </div>
                <IonButton
                  className="hk-digital-add-product-body-steps-card-description-button"
                  onClick={() => addPhoto(PhotoType.MODEL)}
                >
                  <IonIcon color="dark" icon={cameraOutline} />
                  {!modelPhoto ? 'Add Photo' : 'Replace Photo'}
                </IonButton>
                {/* Figma node 8855:5017 — Model Number is a catalog typeahead
                    (not free text): picking a result guarantees a real
                    product_template_id (see the ticket's constraint that
                    typed model/brand text must always tie to a real catalog
                    row). Serial Number has no such constraint — it maps
                    directly to Products.serial, a plain per-product field. */}
                <ProductModelSerialStepFields
                  classPrefix="hk-digital-add-product"
                  typeId={selectedProductType}
                  value={selectedProductTemplateId}
                  valueLabel={selectedProductTemplateLabel}
                  register={register}
                  onModelChange={(result: ProductModelSearchResult) => {
                    setSelectedProductTemplateId(result.id);
                    setSelectedProductTemplateLabel(result.model);
                    // The model result carries its manufacturer's id — auto-fill
                    // Brand from it (informational only; product_template_id
                    // still wins on submit regardless of what Brand shows, so
                    // this can't desync the actual selection, only the displayed
                    // confirmation of it).
                    setSelectedManufacturerId(result.manufacturer_id);
                  }}
                />
              </div>
            </div>
            <div className="hk-digital-add-product-body-steps-card">
              <div className="hk-digital-add-product-body-steps-card-title">
                2: Take a picture of Brand / Manufacturer.
              </div>
              <div className="hk-digital-add-product-body-steps-card-description">
                <span>
                  Typically found on the front or back of the{' '}
                  {productTemplate.label}.
                </span>
                <div className="hk-digital-add-product-body-steps-card-description-image">
                  {!!brandPhoto && (
                    <>
                      <IonImg src={brandPhoto.webviewPath} />
                      <input
                        name="brand_photo"
                        ref={register}
                        type="hidden"
                        value={brandPhoto.webviewPath}
                      />
                    </>
                  )}
                </div>
                <IonButton
                  className="hk-digital-add-product-body-steps-card-description-button"
                  onClick={() => addPhoto(PhotoType.BRAND)}
                >
                  <IonIcon color="dark" icon={cameraOutline} />
                  {!brandPhoto ? 'Add Photo' : 'Replace Photo'}
                </IonButton>
                {/* Figma node 8855:5044 — closed dropdown of the catalog's
                    real manufacturers for this Type (HKSelect, same
                    component the edit form uses for Brand), not free
                    text — same catalog-tie-in constraint as Model Number. */}
                <ProductBrandStepField
                  classPrefix="hk-digital-add-product"
                  value={selectedManufacturerId}
                  manufacturers={manufacturers}
                  onChange={(value) => {
                    setSelectedManufacturerId(value);
                    if (value !== null) {
                      setSelectedProductTemplateId(null);
                      setSelectedProductTemplateLabel(null);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {!!productTemplate?.product_attribute_schema.length &&
          productTemplate.product_attribute_schema
            .filter(
              (attribute: AttributeSchema) =>
                attribute.field.toLowerCase() !== 'serial'
            )
            .map((attribute: AttributeSchema, i: number) => {
              return (
                // Tagged Questions
                <div key={`attribute-${i}`}>
                  {attribute.type === ProductAttributeType.TAGGED ? (
                    <div
                      className="hk-digital-add-product-body-steps-card"
                      key={`tagged-question-${i}`}
                    >
                      <div className="hk-digital-add-product-body-steps-card-title">
                        {`${i + 2}:`} {attribute.label}
                      </div>
                      <div className="hk-digital-add-product-body-steps-card-description">
                        <div className="product-tag-container">
                          {!!attribute.allow &&
                            attribute.allow.map((tag: string, i: number) => (
                              <div
                                className={`product-tag ${
                                  checkIfProductTagSelected(
                                    attribute.field,
                                    tag
                                  )
                                    ? 'product-tag-selected'
                                    : ''
                                }`}
                                key={`product-tag-${i}`}
                                onClick={() =>
                                  selectProductTag(attribute.field, tag)
                                }
                              >
                                {tag}
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Text Input Questions
                    <div
                      className="hk-digital-add-product-body-steps-card"
                      key={`input-question-${i}`}
                    >
                      <div className="hk-digital-add-product-body-steps-card-title">
                        {`${i + 3}:`} {attribute.label}
                      </div>
                      <div className="hk-digital-add-product-body-steps-card-description">
                        <IonInput
                          name={attribute.field}
                          ref={register}
                          className="hk-digital-add-product-input"
                          placeholder="Enter here..."
                        ></IonInput>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
      </>
    );
  }

  function DesktopInventoryAddProductView() {
    return (
      <>
        <HkModalHeader
          title="Add an Item"
          onClose={() => dispatch(hideAddItemModal())}
        />
        <IonContent className="hk-desktop-digital-add-product">
          {loading ? (
            <div className="hk-loading-spinner">
              <IonSpinner name="crescent" color="primary" />
            </div>
          ) : (
            <>
              {showLanding ? (
                <InventoryAddItemLanding
                  productTypeGroupsList={productTypeGroupsList}
                  onModelMatched={onModelMatched}
                  onManualAdd={() => setShowLanding(false)}
                />
              ) : (
                <form onSubmit={handleSubmit(addProduct)}>
                  <div className="hk-desktop-digital-add-product-body">
                    <ProductGroupTypeSelects
                      classPrefix="hk-desktop-digital-add-product-body"
                      productTypeGroupsList={productTypeGroupsList}
                      selectedProductGroup={selectedProductGroup}
                      selectedProductType={selectedProductType}
                      onGroupChange={(groupId) => {
                        setSelectedProductGroup(groupId);
                        setSelectedProductType(null);
                        setSelectedProductTemplateId(null);
                        setSelectedProductTemplateLabel(null);
                        setSelectedManufacturerId(null);
                      }}
                      onTypeChange={(typeId) => {
                        setSelectedProductType(typeId);
                        setSelectedProductTemplateId(null);
                        setSelectedProductTemplateLabel(null);
                        setSelectedManufacturerId(null);
                      }}
                    />
                    {!!selectedProductGroup &&
                      !!selectedProductType &&
                      AddProductQuestions()}
                  </div>
                </form>
              )}
            </>
          )}
        </IonContent>
        <div
          className={`hk-desktop-digital-add-product-toolbar${
            showLanding ? ' hk-add-item-toolbar-landing' : ''
          }`}
        >
          {showLanding ? (
            <IonButton
              className="hk-inventory-add-item-landing-cancel-button"
              fill="outline"
              onClick={() => dispatch(hideAddItemModal())}
            >
              Cancel
            </IonButton>
          ) : (
            <>
              <IonButton
                color="white"
                strong={true}
                disabled={loading}
                onClick={goBackToModelSearch}
              >
                Back
              </IonButton>
              <div className="hk-desktop-digital-add-product-toolbar-actions">
                <IonButton
                  color="white"
                  strong={true}
                  className="ion-margin-vertical"
                  disabled={loading}
                  onClick={cancelAddProduct}
                >
                  Cancel
                </IonButton>
                <IonButton
                  color="primary"
                  type="submit"
                  strong={true}
                  onClick={handleSubmit(addProduct)}
                  disabled={!productTemplate || !modelPhoto || loading}
                >
                  {loading ? <IonSpinner name="dots" /> : 'Add Item'}
                </IonButton>
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  function MobileInventoryAddProductView() {
    return (
      <>
        <HkModalHeader
          title="Add an Item"
          onClose={() => dispatch(hideAddItemModal())}
          onBack={showLanding ? undefined : goBackToModelSearch}
        />
        <IonContent className="hk-digital-add-product">
          {loading ? (
            <div className="hk-loading-spinner">
              <IonSpinner name="crescent" color="primary" />
            </div>
          ) : (
            <>
              {showLanding ? (
                <InventoryAddItemLanding
                  productTypeGroupsList={productTypeGroupsList}
                  onModelMatched={onModelMatched}
                  onManualAdd={() => setShowLanding(false)}
                />
              ) : (
                <form onSubmit={handleSubmit(addProduct)}>
                  <div className="hk-digital-add-product-body">
                    <ProductGroupTypeSelects
                      classPrefix="hk-digital-add-product-body"
                      productTypeGroupsList={productTypeGroupsList}
                      selectedProductGroup={selectedProductGroup}
                      selectedProductType={selectedProductType}
                      onGroupChange={(groupId) => {
                        setSelectedProductGroup(groupId);
                        setSelectedProductType(null);
                        setSelectedProductTemplateId(null);
                        setSelectedProductTemplateLabel(null);
                        setSelectedManufacturerId(null);
                      }}
                      onTypeChange={(typeId) => {
                        setSelectedProductType(typeId);
                        setSelectedProductTemplateId(null);
                        setSelectedProductTemplateLabel(null);
                        setSelectedManufacturerId(null);
                      }}
                    />
                    {!!selectedProductGroup &&
                      !!selectedProductType &&
                      AddProductQuestions()}
                  </div>
                </form>
              )}
            </>
          )}
        </IonContent>
        <IonToolbar
          className={`hk-digital-add-product-toolbar${
            showLanding ? ' hk-add-item-toolbar-landing' : ''
          }`}
        >
          {showLanding ? (
            <IonButton
              className="hk-inventory-add-item-landing-cancel-button"
              fill="outline"
              onClick={() => dispatch(hideAddItemModal())}
            >
              Cancel
            </IonButton>
          ) : (
            <div className="hk-digital-add-product-toolbar-actions">
              <IonButton
                color="white"
                strong={true}
                disabled={loading}
                onClick={cancelAddProduct}
              >
                Cancel
              </IonButton>
              <IonButton
                color="primary"
                type="submit"
                strong={true}
                onClick={handleSubmit(addProduct)}
                disabled={!productTemplate || !modelPhoto || loading}
              >
                {loading ? <IonSpinner name="dots" /> : 'Add Item'}
              </IonButton>
            </div>
          )}
        </IonToolbar>
      </>
    );
  }

  return (
    <>
      {platformType === HKPlatform.DESKTOP && isDesktopWidth
        ? DesktopInventoryAddProductView()
        : MobileInventoryAddProductView()}
    </>
  );
};

export default InventoryAddProduct;
